import React, { useCallback, useEffect, useState } from 'react';
import LoadingSpiner from '../Loader/LoadingSpiner';
import { useDispatch, useSelector } from 'react-redux';
import { ImageSubmissionStatus, clearSelectedImageIds, clearSubmissionLists, selectImageIdsState, selectImageSubmissionStatusList, selectImagesToBeSubmittedForAnalysis, selectModelName, setImageSubmissionStatusList, setImageSubmissionStatusListFromResults, setImageToBeSubmittedForAnalysis } from '../../Store/imageSlice';
import { useAnalyzeImagesBatchMutation } from '../../api/image';
import { FaCircleCheck, FaCircleXmark, FaMicroscope, FaRadio, FaRegSquareCheck } from 'react-icons/fa6';
import ActionCard from '../../Layouts/ActionCard';
import ImageAnalyzeButtons from './ImageAnalyzeButtons';
import { useImageList } from './hooks/useImageList';

interface ImageAnalyzeModalProps {
    projectId: string;
    closeModal: () => void;
    clearSelectionOnClose?: boolean;
}

export default function ImageAnalyzeModal(props: ImageAnalyzeModalProps) {

    const { projectId, closeModal, clearSelectionOnClose } = props;

    const { images, isLoading: isImagesLoading, isError: isImagesError } = useImageList({ 
        projectId, 
        autoFetchAll: true // Load all images for analysis selection
    });
    console.log("images", images);
    const [analyzeImagesBatch, { isLoading: isAnalyzing, isError: isAnalyzeError }] = useAnalyzeImagesBatchMutation();
    const selectedImageIds = useSelector(selectImageIdsState);
    const imagesToBeSubmittedFromState = useSelector(selectImagesToBeSubmittedForAnalysis);
    const imagesSubmissionStatusList = useSelector(selectImageSubmissionStatusList);
    const modelName = useSelector(selectModelName);
    console.log("model name selected", modelName)
    const [confirmedSubmit, setConfirmedSubmit] = useState(false);
    const [allowPoorQuality, setAllowPoorQuality] = useState(false);

    const dispatch = useDispatch();

    const handleAnalyzeSubmission = useCallback(async (imageList: ImageSubmissionStatus[]) => {
        setConfirmedSubmit(true);
        if (!imageList.length) return;

        const imagesForBatch = imageList.map(({ imageId }) => {
            // fall back to imageId if the image isn't in the loaded list (stale cache / just uploaded);
            // server keys on imageId, so a missing display name shouldn't false-error the submission
            const name = images.find((image: any) => image.id === imageId)?.name || imageId;
            return {
                imageId,
                resultName: `${name} - ${modelName} - ${new Date().toISOString()}`,
            };
        });

        try {
            const response = await analyzeImagesBatch({
                images: imagesForBatch,
                engine: "v2",
                segmentationType: "semantic",
                modelName,
                forceAnalyze: allowPoorQuality,
            }).unwrap();

            // completion (success/failure per image) is reported later by
            // email, not here - this only reflects whether each image was
            // accepted for analysis
            dispatch(setImageSubmissionStatusListFromResults(response.results));
        } catch (error: any) {
            console.log('error in analyzeImagesBatch', error);
            const reason = error?.data?.message || error?.error;
            dispatch(setImageSubmissionStatusListFromResults(
                imageList.map(({ imageId }) => ({ imageId, accepted: false, reason }))
            ));
        }
    }, [images, modelName, allowPoorQuality]);

    useEffect(() => {
        if (selectedImageIds.length && !imagesToBeSubmittedFromState.length) {
            dispatch(setImageToBeSubmittedForAnalysis(selectedImageIds))
        }
    }, [selectedImageIds]);

    useEffect(() => {
        console.log('imagesToBeSubmitted', imagesToBeSubmittedFromState);
        if (imagesToBeSubmittedFromState.length && !imagesSubmissionStatusList.length) {
            const imageStatusList = imagesToBeSubmittedFromState.map((imageId: string) => {
                return {
                    imageId,
                    status: 'pending'
                }
            });

            console.log('imageStatusList', imageStatusList);
            dispatch(setImageSubmissionStatusList(imageStatusList));
        }
    }, [imagesToBeSubmittedFromState]);

    useEffect(() => {
        return () => {
            // clear the imagesToBeSubmittedFromState list
            dispatch(clearSubmissionLists());
            // clear selection only when the opener seeded it (e.g. EmptyResult single-image),
            // so list-view checkbox selection survives a cancelled modal
            if (clearSelectionOnClose) dispatch(clearSelectedImageIds());
        }
    }, []);

    if (isImagesLoading) return <div className='alert alert-base rounded-md p-2'><LoadingSpiner /> Loading images...</div>;
    if (isImagesError) return <div className='alert alert-error rounded-md p-2'>Error loading images</div>;
    if (!imagesToBeSubmittedFromState.length) return <div className='alert alert-base rounded-md p-2'>Please select the images you'd lke to run analysis on.</div>;
    if (!imagesSubmissionStatusList.length) return <div className='alert alert-base rounded-md p-2'><LoadingSpiner /> Loading ...</div>;

    const allDone = imagesSubmissionStatusList.every((imageSubmissionStatus: ImageSubmissionStatus) => imageSubmissionStatus.status !== 'pending');
    const imagesWithError = imagesSubmissionStatusList.filter((imageSubmissionStatus: ImageSubmissionStatus) => imageSubmissionStatus.status === 'error');

    if(!confirmedSubmit){
        return (
            <div className="container mx-auto">
                <div className="grid grid-cols-1 gap-4 mt-4">
                    <p className='font-bold'>Select an analysis model</p>
                    <ImageAnalyzeButtons />
                    <div className="form-control">
                        <label className="label cursor-pointer">
                            <span className="label-text">{allowPoorQuality ? 'Including slides of all quality levels in the analysis.' : 'Excluding slides of poor quality from the analysis.'}</span>
                            <input 
                                type="checkbox" 
                                className="toggle toggle-primary" 
                                checked={allowPoorQuality}
                                onChange={(e) => setAllowPoorQuality(e.target.checked)}
                            />
                        </label>
                    </div>
                    <p className='font-bold'>Review the images to submit</p>
                    {
                        imagesSubmissionStatusList.map(({ imageId, status }: ImageSubmissionStatus) => {
                            const name = images.find((image: any) => image.id === imageId)?.name;
                            return <div key={imageId} className='alert alert-base rounded-md p-2'><FaRegSquareCheck />{name}</div>
                        })
                    }
                    <div className="flex justify-end mt-4">
                        <button className="btn mr-2 btn-default" onClick={closeModal}>Cancel</button>
                        <button className="btn bg-black text-white" onClick={() => handleAnalyzeSubmission(imagesSubmissionStatusList)}>Confirm Submit</button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container mx-auto">
            {/* {isAnalyzing && <div className='alert alert-base'><LoadingSpiner /> Analyzing images...</div>} */}
            {isAnalyzeError && allDone && <div className='alert alert-error rounded-md p-2'>Error submitting {imagesWithError.length} images.</div>}
            <div className="grid grid-cols-1 gap-4 mt-4">
            {
                imagesSubmissionStatusList.map(({ imageId, status, reason }: ImageSubmissionStatus) => {
                    const name = images.find((image: any) => image.id === imageId)?.name;
                    if (status === 'pending') return <div key={imageId} className='alert alert-base rounded-md p-2'><LoadingSpiner /> Sending {name} for analysis...</div>
                    if (status === 'success') return <div key={imageId} className='alert alert-success rounded-md p-2'><FaCircleCheck /> {name} sent for analysis</div>
                    if (status === 'error') return <div key={imageId} className='alert alert-error rounded-md p-2'><FaCircleXmark /> Error sending {name} for analysis{reason ? `: ${reason}` : ''}</div>
                    return <div key={imageId} className='alert alert-base rounded-md p-2'><FaCircleXmark /> Unable to send {name} for analysis...</div>
                })
            }
            </div>
        </div>
    )
}